import type { ClobMarketConfig } from '@cpc/tx';
import { DGC_TYPE, mistToDgc } from '@cpc/tx';

function viteEnv(name: string): string | undefined {
	const v = import.meta.env[name];
	return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** Hub GET /v1/stack-config (subset used for CLOB markets). */
export type HubStackConfig = {
	rpcUrl?: string;
	cpcCoinType?: string;
	clob?: {
		packageId?: string;
		registryId?: string;
		orderBookId?: string;
		dataTypeTag?: string;
		pair?: string;
	};
	clobCpcData?: {
		packageId?: string;
		registryId?: string;
		orderBookId?: string;
		dataTypeTag?: string;
		cpcTypeTag?: string;
		pair?: string;
		pairId?: number;
		dataPerCpc?: number;
	};
};

/**
 * Build trade markets from Hub stack-config.
 * Authority: Hub — frontend must not invent pairs from build-time env.
 */
export function marketsFromStackConfig(sc: HubStackConfig | null | undefined): ClobMarketConfig[] {
	if (!sc?.clob?.packageId || !sc.clob.registryId || !sc.clob.orderBookId) {
		return [];
	}
	const packageId = sc.clob.packageId;
	const registryId = sc.clob.registryId;
	const dataTypeTag =
		sc.clob.dataTypeTag ||
		sc.clobCpcData?.dataTypeTag ||
		`${packageId}::data::DATA`;
	const out: ClobMarketConfig[] = [
		{
			marketId: 'dgc-data',
			packageId,
			registryId,
			orderBookId: sc.clob.orderBookId,
			dataTypeTag,
			baseType: sc.cpcCoinType || DGC_TYPE,
			baseSymbol: 'DGC',
			quoteSymbol: 'DATA',
			referencePrice: '1',
			hubPair: 'DATA-CPC',
		},
	];
	const cpc = sc.clobCpcData;
	if (cpc?.orderBookId && cpc.cpcTypeTag) {
		out.push({
			marketId: 'cpc-data',
			packageId: cpc.packageId || packageId,
			registryId: cpc.registryId || registryId,
			orderBookId: cpc.orderBookId,
			dataTypeTag: cpc.dataTypeTag || dataTypeTag,
			baseType: cpc.cpcTypeTag,
			baseSymbol: 'CPC',
			quoteSymbol: 'DATA',
			referencePrice: cpc.dataPerCpc != null ? String(cpc.dataPerCpc) : '0.3',
			hubPair: 'CPC-DATA',
		});
	}
	return out;
}

/** Fetch live markets from Push Hub (source of truth). */
export async function fetchClobMarketsFromHub(
	signal?: AbortSignal,
): Promise<ClobMarketConfig[]> {
	const base = getPushHubHttpUrl();
	const res = await fetch(`${base}/v1/stack-config`, { signal });
	if (!res.ok) {
		throw new Error(`Hub stack-config HTTP ${res.status}`);
	}
	const sc = (await res.json()) as HubStackConfig;
	return marketsFromStackConfig(sc);
}

/**
 * Offline / build-time fallback only (Hub unreachable).
 * Prefer {@link fetchClobMarketsFromHub} for the trade UI.
 */
export function getClobMarketsFromVite(): ClobMarketConfig[] {
	const packageId = viteEnv('VITE_CPC_CLOB_PACKAGE_ID');
	const registryId = viteEnv('VITE_CPC_REGISTRY_ID');
	const orderBookId = viteEnv('VITE_CPC_ORDER_BOOK_ID');
	const dataTypeTag =
		viteEnv('VITE_CPC_DATA_COIN_TYPE') ??
		(packageId ? `${packageId}::data::DATA` : undefined);
	const out: ClobMarketConfig[] = [];
	if (packageId && registryId && orderBookId && dataTypeTag) {
		out.push({
			marketId: 'dgc-data',
			packageId,
			registryId,
			orderBookId,
			dataTypeTag,
			baseType: viteEnv('VITE_CPC_DGC_COIN_TYPE') ?? DGC_TYPE,
			baseSymbol: 'DGC',
			quoteSymbol: 'DATA',
			referencePrice: '1',
			hubPair: 'DATA-CPC',
		});
	}
	const cpcBook = viteEnv('VITE_CPC_CPC_ORDER_BOOK_ID');
	const cpcType = viteEnv('VITE_CPC_APP_COIN_TYPE');
	if (packageId && registryId && cpcBook && dataTypeTag && cpcType) {
		out.push({
			marketId: 'cpc-data',
			packageId,
			registryId,
			orderBookId: cpcBook,
			dataTypeTag,
			baseType: cpcType,
			baseSymbol: 'CPC',
			quoteSymbol: 'DATA',
			referencePrice: viteEnv('VITE_CPC_CPC_DATA_REF_PRICE') ?? '0.3',
			hubPair: 'CPC-DATA',
		});
	}
	return out;
}

/** @deprecated Sync snapshot — trade page should use fetchClobMarketsFromHub. */
export function getClobMarkets(): ClobMarketConfig[] {
	return getClobMarketsFromVite();
}

/** Legacy single-market helper (vite fallback). */
export function getClobMarketConfig(): ClobMarketConfig | null {
	return getClobMarketsFromVite()[0] ?? null;
}

export function getCpcRpcUrl(): string {
	return viteEnv('VITE_CPC_RPC_URL') ?? 'https://rpc.dgclabs.cc';
}

export function getPushHubWsUrl(): string {
	return viteEnv('VITE_CPC_PUSH_HUB_WS_URL') ?? 'wss://dgclabs.cc/wss';
}

export function getPushHubHttpUrl(): string {
	const raw = viteEnv('VITE_CPC_PUSH_HUB_HTTP_URL') ?? 'https://dgclabs.cc/hub';
	return raw.replace(/\/health\/?$/i, '').replace(/\/$/, '');
}

export function getIndexerRpcUrl(): string | undefined {
	return viteEnv('VITE_CPC_INDEXER_RPC_URL');
}

export function getCpcCoinType(): string {
	return viteEnv('VITE_CPC_DGC_COIN_TYPE') ?? '0x2::dgc::DGC';
}

/** Bridge / CLOB DATA type. Mainnet fallback matches published `data::DATA`. */
export const DEFAULT_DATA_COIN_TYPE =
	'0xe3129e7a8e43d32c967d751f037028797e0be1328204d73b917420ba7ede2a6f::data::DATA';

export function getDataCoinType(): string {
	return viteEnv('VITE_CPC_DATA_COIN_TYPE') ?? getClobMarketConfig()?.dataTypeTag ?? DEFAULT_DATA_COIN_TYPE;
}

/** App CPC fungible (not native DGC). */
export function getAppCpcCoinType(): string | undefined {
	return viteEnv('VITE_CPC_APP_COIN_TYPE');
}

/** 链上 PairConfig.lot_size。链改成 1 mist 后须同步 `VITE_CPC_CLOB_LOT_SIZE=1`。 */
export function getClobLotSizeMist(): bigint {
	const raw = viteEnv('VITE_CPC_CLOB_LOT_SIZE') ?? '1000000000';
	try {
		const n = BigInt(raw);
		return n > 0n ? n : 1_000_000_000n;
	} catch {
		return 1_000_000_000n;
	}
}

/** 改一手之前：1 lot = 1 枚 = 1e9 mist。 */
export const LEGACY_CLOB_LOT_SIZE_MIST = 1_000_000_000n;

/** pair 1 `set_lot_size(1)` 成功时间（FY2ofCXrqfQUGmqWiLXh6ZCrDazsF4Rz99uNaFPcjxp）。 */
export const CLOB_LOT_CUTOVER_MS = 1_790_835_231_274;

/** 有成交/挂单时间则按切日前 1e9、切日后当前一手；盘口无时间戳用当前一手。 */
export function resolveClobLotSizeMist(
	qtyLots?: string | number | bigint | null,
	timestampMs?: number | null,
): bigint {
	const current = getClobLotSizeMist();
	if (current >= LEGACY_CLOB_LOT_SIZE_MIST) return current;
	const ts = timestampMs == null ? Number.NaN : Number(timestampMs);
	if (Number.isFinite(ts) && ts > 0) {
		return ts < CLOB_LOT_CUTOVER_MS ? LEGACY_CLOB_LOT_SIZE_MIST : current;
	}
	return current;
}

export function qtyLotsToTokenMist(
	qtyLots: string | number | bigint | null | undefined,
	timestampMs?: number | null,
): bigint {
	const lots = BigInt(qtyLots ?? 0);
	return lots * resolveClobLotSizeMist(lots, timestampMs);
}

/** 链上 qty_lots → 人类可读枚数。历史单传入 timestampMs，避免旧手数显示成 0.00000000x。 */
export function formatClobQtyLots(
	qtyLots: string | number | bigint | null | undefined,
	timestampMs?: number | null,
): string {
	try {
		return mistToDgc(qtyLotsToTokenMist(qtyLots, timestampMs));
	} catch {
		return '0';
	}
}
